import { useEffect, useState } from 'react';
import { useApi } from '../apiHelpers';
import { formatEventDate } from '../utils/date-utils';
import AirspaceStatusPill from '../components/AirspaceStatusPill';
import { AirspaceStatus } from '../utils/airspace-helpers';
import Tag from '../components/ui/Tag';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

interface NearbyEvent {
  id: string;
  name: string;
  event_date: string;
  location: string | null;
  controlling_facility: string | null;
  airspace_status: AirspaceStatus;
  distance_nm: number | null;
  match_reasons: Array<{ kind: 'radius' | 'facility'; distance_nm?: number; facility?: string }>;
}

interface Props {
  lat: number;
  lng: number;
  facility?: string | null;
  excludeEventId: string;
  onSelect: (id: string) => void;
}

const NearbyHistoricalEvents: React.FC<Props> = ({ lat, lng, facility, excludeEventId, onSelect }) => {
  const api = useApi();
  const [items, setItems] = useState<NearbyEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ lat: String(lat), lng: String(lng), exclude_event_id: excludeEventId });
    if (facility) params.set('facility', facility);
    api.get(`/airspace/nearby?${params}`, { silent: true })
      .then((d: NearbyEvent[]) => { if (!cancelled) { setItems(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, facility, excludeEventId]);

  return (
    <div>
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-1 h-3.5 rounded-full bg-amber-500/60" />
        <h3 className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold">Nearby History</h3>
      </div>
      {loading && (
        <SkeletonGroup label="Loading nearby history" className="space-y-2">
          {[0, 1].map(i => <Skeleton key={i} className="h-14 w-full" />)}
        </SkeletonGroup>
      )}
      <ListStack
        items={loading ? null : items}
        getKey={it => it.id}
        spacing="loose"
        label="Nearby historical events"
        empty="No prior events within 25 nm or controlling facility."
        renderRow={it => (
          <ListRow onClick={() => onSelect(it.id)}>
            <span className="block">
              <span className="flex items-start justify-between gap-2">
                <span className="min-w-0">
                  <span className="block text-xs font-medium text-on-surface truncate">{it.name}</span>
                  <span className="block text-[11px] text-on-surface-variant mt-0.5">{formatEventDate(it.event_date)}</span>
                </span>
                <AirspaceStatusPill status={it.airspace_status} size="table" />
              </span>
              <span className="text-[10px] text-on-surface-variant mt-1.5 flex flex-wrap gap-1">
                {it.match_reasons.map((r, i) => (
                  <Tag key={i} variant="code" size="sm">
                    {r.kind === 'radius' ? `${r.distance_nm} nm` : `↦ ${r.facility}`}
                  </Tag>
                ))}
              </span>
            </span>
          </ListRow>
        )}
      />
    </div>
  );
};

export default NearbyHistoricalEvents;
